import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken,
  signInWithCustomToken,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

export interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'hr_manager' | 'manager' | 'employee';
  department?: string;
  permissions: string[];
}

export interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  hasPermission: (permission: string) => boolean;
  canViewEmployeeDetails: (employeeId?: number) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define role-based permissions
const ROLE_PERMISSIONS = {
  admin: [
    'view_all_employees',
    'edit_all_employees',
    'delete_employees',
    'view_payroll',
    'manage_payroll',
    'view_hr_analytics',
    'manage_system_settings'
  ],
  hr_manager: [
    'view_all_employees',
    'edit_all_employees',
    'view_payroll',
    'manage_payroll',
    'view_hr_analytics',
    'manage_employee_records'
  ],
  manager: [
    'view_team_employees',
    'edit_team_employees',
    'view_team_payroll',
    'approve_time_entries',
    'approve_leave_requests'
  ],
  employee: [
    'view_own_profile',
    'edit_own_profile',
    'view_own_payroll',
    'submit_time_entries',
    'request_leave'
  ]
};

// Mock users for demonstration
const MOCK_USERS: User[] = [
  {
    id: 1,
    name: 'Admin User',
    email: 'admin@techflowsolutions.co.uk',
    role: 'admin',
    permissions: ROLE_PERMISSIONS.admin
  },
  {
    id: 2,
    name: 'HR Manager',
    email: 'hr@techflowsolutions.co.uk',
    role: 'hr_manager',
    department: 'HR',
    permissions: ROLE_PERMISSIONS.hr_manager
  },
  {
    id: 3,
    name: 'Sales Manager',
    email: 'sales.manager@techflowsolutions.co.uk',
    role: 'manager',
    department: 'Sales',
    permissions: ROLE_PERMISSIONS.manager
  },
  {
    id: 4,
    name: 'Regular Employee',
    email: 'employee@techflowsolutions.co.uk',
    role: 'employee',
    department: 'Sales',
    permissions: ROLE_PERMISSIONS.employee
  }
];

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔍 Setting up Firebase auth state listener...');

    // Check for Admin-minted SSO custom tokens from the portal.
    const checkIncomingToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const customToken = urlParams.get('customToken');
      const legacyToken = urlParams.get('token') || urlParams.get('sso_token');
      const cleanUrl = () => {
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${window.location.hash}`
        );
      };

      // Option C: Firebase Custom Token — sign in directly so
      // onAuthStateChanged below picks up a real Firebase session.
      if (customToken) {
        console.log('🔐 SSO custom token detected — signing in via Firebase...');
        cleanUrl();
        try {
          await signInWithCustomToken(auth, customToken);
          console.log('✅ Firebase signInWithCustomToken OK');
        } catch (err) {
          console.error('❌ signInWithCustomToken failed:', err);
        }
        return;
      }

      if (legacyToken) {
        console.warn('Rejected legacy raw-token SSO link.');
        cleanUrl();
      }
    };

    checkIncomingToken();

    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 Auth state changed:', firebaseUser ? `User: ${firebaseUser.email}` : 'No user');
      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        try {
          // Get Firebase ID token
          const token = await getIdToken(firebaseUser);
          localStorage.setItem('firebaseToken', token);
          localStorage.setItem('auth_token', token); // For backward compatibility
          console.log('✅ Firebase token stored for user:', firebaseUser.email);

          // Fetch user data from backend using Firebase token
          // The backend will create/update the user record based on Firebase UID
          const response = await fetch('/api/v1/users/firebase-me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            console.log('✅ User data fetched from backend:', userData);

            // Map backend user data to frontend User interface
            const mappedUser: User = {
              id: userData.id,
              name: userData.username || firebaseUser.displayName || firebaseUser.email || 'User',
              email: userData.email || firebaseUser.email || '',
              role: userData.is_admin ? 'admin' : 'employee',
              department: userData.department,
              permissions: userData.is_admin ? ROLE_PERMISSIONS.admin : ROLE_PERMISSIONS.employee
            };

            setUser(mappedUser);
            localStorage.setItem('currentUser', JSON.stringify(mappedUser));
          } else {
            // If backend auth fails, use Firebase user data with limited permissions
            console.warn('⚠️ Backend auth failed, using Firebase user data with limited access');
            const fallbackUser: User = {
              id: 0,
              name: firebaseUser.displayName || firebaseUser.email || 'User',
              email: firebaseUser.email || '',
              role: 'employee',
              department: 'General',
              permissions: ROLE_PERMISSIONS.employee
            };
            setUser(fallbackUser);
            localStorage.setItem('currentUser', JSON.stringify(fallbackUser));
          }
        } catch (error) {
          console.error('❌ Error fetching user data:', error);
        }
      } else {
        setUser(null);
        localStorage.removeItem('firebaseToken');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('currentUser');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      console.log('🔵 Starting Google sign-in popup...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ Sign-in popup completed');

      const token = await getIdToken(result.user);
      localStorage.setItem('firebaseToken', token);
      localStorage.setItem('auth_token', token);
      console.log('✅ Firebase sign-in successful, user:', result.user.email);
      console.log('✅ Token stored, auth state will update automatically');
    } catch (error: any) {
      console.error('❌ Error signing in with Google:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  };

  const login = async (email: string, _password: string): Promise<boolean> => {
    // Legacy login support - kept for backward compatibility
    // In production, this should redirect to Firebase auth
    console.warn('⚠️  Legacy login called - redirecting to Firebase auth');
    await signInWithGoogle();
    return true;
  };

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('firebaseToken');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('currentUser');
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const getToken = async (): Promise<string | null> => {
    if (!firebaseUser) return null;
    try {
      const token = await getIdToken(firebaseUser, true); // Force refresh
      localStorage.setItem('firebaseToken', token);
      localStorage.setItem('auth_token', token);
      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const canViewEmployeeDetails = (employeeId?: number): boolean => {
    if (!user) return false;

    // Admin and HR Manager can view all employee details
    if (hasPermission('view_all_employees')) {
      return true;
    }

    // Managers can view their team members (simplified - in real app would check actual reporting structure)
    if (hasPermission('view_team_employees')) {
      return true; // For demo, allow managers to view all
    }

    // Employees can only view their own details
    if (hasPermission('view_own_profile') && employeeId) {
      return employeeId === user.id;
    }

    return false;
  };

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    signInWithGoogle,
    login,
    logout,
    getToken,
    hasPermission,
    canViewEmployeeDetails
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper component for role-based rendering
export const ProtectedComponent: React.FC<{
  permission?: string;
  role?: User['role'];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ permission, role, fallback = null, children }) => {
  const { user, hasPermission } = useAuth();

  if (!user) return <>{fallback}</>;

  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  if (role && user.role !== role) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Quick role switcher for demo purposes
export const RoleSwitcher: React.FC = () => {
  const { user, login } = useAuth();

  const switchRole = async (newUser: User) => {
    await login(newUser.email, 'demo');
  };

  return (
    <div style={{ position: 'fixed', top: 10, right: 10, zIndex: 9999, background: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
      <div style={{ fontSize: '12px', marginBottom: '5px' }}>
        Current: {user?.name} ({user?.role})
      </div>
      <select 
        value={user?.id || ''} 
        onChange={(e) => {
          const selectedUser = MOCK_USERS.find(u => u.id === parseInt(e.target.value));
          if (selectedUser) switchRole(selectedUser);
        }}
        style={{ fontSize: '12px' }}
      >
        {MOCK_USERS.map(u => (
          <option key={u.id} value={u.id}>
            {u.name} ({u.role})
          </option>
        ))}
      </select>
    </div>
  );
};
